import React, { useEffect, useState } from "react";
import { api } from "./api";
import { DISPOSITION_LABEL, stamp } from "./Pass";

/**
 * "To countersign" (build plan §9.1 #8): the doses someone else entered
 * naming you as the one who gave them. Each stays unverified until you
 * countersign it here, signed in as yourself. One at a time, or every row
 * shown.
 */

interface Row {
  adminKey: string;
  personKey: string;
  lastName: string;
  firstName: string;
  medicationName: string;
  windowCode: string;
  scheduledOn: string;
  disposition: string;
  amountGiven: string;
  givenAtUtc: string;
  enteredByEmail: string;
  enteredAtUtc: string;
  fromPaper: boolean;
}

interface VisitRow {
  visitKey: string;
  personName: string;
  kind: string;
  complaint: string;
  startedAtUtc: string;
  recordedByName: string;
  recordedByEmail: string;
}

export function Countersign(): React.ReactElement {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [visits, setVisits] = useState<VisitRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    void api<{ rows: Row[]; visits?: VisitRow[] }>("GET", "/api/countersign").then((r) => (r.ok ? (setRows(r.data.rows), setVisits(r.data.visits ?? [])) : setError(r.data.error ?? "Could not load.")));
  }, []);
  async function sign(keys: string[]) {
    setBusy(true);
    setError(null);
    const r = await api<{ countersigned: number; rows: Row[] }>("POST", "/api/countersign", { adminKeys: keys });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not countersigned.");
    setRows(r.data.rows);
    setMsg(`Countersigned ${r.data.countersigned}.`);
  }
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">To countersign</h2>
      <p className="mb-3 text-sm text-slate-600">Doses entered by someone else that name you as the one who gave them. Check each, then countersign: until you do, they show as unverified.</p>
      {error && <p className="field-error" role="alert">{error}</p>}
      {msg && <p className="mb-2 text-sm text-emerald-800" role="status">{msg}</p>}
      {!rows && !error && <p className="text-sm text-slate-600">Loading…</p>}
      {rows && (
        <div className="section-card">
          {rows.length === 0 ? (
            <p className="text-sm text-slate-600">Nothing to countersign.</p>
          ) : (
            <>
              <ul className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <li key={r.adminKey} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="font-medium">{r.lastName}, {r.firstName}</span> · {r.medicationName} · {DISPOSITION_LABEL[r.disposition] ?? r.disposition}{r.amountGiven ? ` ${r.amountGiven}` : ""}
                      <span className="block text-xs text-slate-500">
                        {r.windowCode ? `${r.windowCode} · ` : ""}given {stamp(r.givenAtUtc)} · entered by {r.enteredByEmail} {stamp(r.enteredAtUtc)}{r.fromPaper ? " (from paper)" : ""}
                      </span>
                    </span>
                    <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={() => sign([r.adminKey])}>Countersign</button>
                  </li>
                ))}
              </ul>
              <button type="button" className="btn-primary mt-3 min-h-11" disabled={busy} onClick={() => sign(rows.map((r) => r.adminKey))}>Countersign all {rows.length} shown</button>
            </>
          )}
        </div>
      )}
      {visits.length > 0 && (
        <div className="section-card">
          <h3 className="mb-1 text-base font-semibold">Visits recorded by someone not licensed</h3>
          <p className="mb-2 text-xs text-slate-600">Any licensed user countersigns these (never your own). Open the visit to check it.</p>
          <ul className="divide-y divide-slate-100">
            {visits.map((v) => (
              <li key={v.visitKey} className="py-2 text-sm">
                <a className="font-medium text-program underline" href={`/visits/${encodeURIComponent(v.visitKey)}`}>{v.personName}</a> · {v.kind} · {v.complaint}
                <span className="block text-xs text-slate-500">{stamp(v.startedAtUtc)} · recorded by {v.recordedByName || v.recordedByEmail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
